"use client"

import type { Enemy, EnemyTypeConfig } from "@/types/enemy.types"
import { ENEMY_SPRITESHEET, ENEMY_SPRITE_INDEX } from "@/components/game-ui/enemy-sprite-index"

const SPRITE_SIZE = 64 // Each portrait is 64x64 pixels


interface EnemyPortraitSectionProps {
  enemyConfig: EnemyTypeConfig
  enemy: Enemy
  onDeselectEnemy?: () => void
  scale: number
}

export const EnemyPortraitSection = ({
  enemyConfig,
  enemy,
  onDeselectEnemy,
  scale,
}: EnemyPortraitSectionProps) => {
  const effectiveScale = scale > 0 ? scale : 1
  const width = 84 * effectiveScale
  const height = 84 * effectiveScale
  const left = 19 * effectiveScale
  const top = 26 * effectiveScale

  // Get sprite index for this enemy type
  const spriteIndex = ENEMY_SPRITE_INDEX[enemy.type] ?? 0
  const spriteOffsetX = spriteIndex * SPRITE_SIZE

  // Determine border style based on variant
  const getBorderStyle = () => {
    if (enemy.variant === "unique") {
      return {
        boxShadow: "inset 0 0 0 3px #F97316", // Orange border for unique
      }
    }
    if (enemy.variant === "elite") {
      return {
        boxShadow: "inset 0 0 0 2px #A855F7", // Purple border for elite
      }
    }
    return {} // No border for regular
  }

  return (
    <div
      id="enemy-portrait-section"
      className="absolute"
      style={{
        left,
        top,
        width,
        height,
      }}
    >
      <button
        id="enemy-portrait-button"
        onClick={onDeselectEnemy}
        className="w-full h-full relative overflow-hidden transition-all hover:brightness-110"
        title="Click to deselect enemy"
      >
        {/* Enemy portrait from spritesheet */}
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundImage: `url(${ENEMY_SPRITESHEET})`,
            backgroundPosition: `-${spriteOffsetX * (width / SPRITE_SIZE)}px 0`,
            backgroundSize: `${16 * width}px ${height}px`,
            imageRendering: "pixelated",
            ...getBorderStyle(),
          }}
          aria-label={`${enemyConfig.name} portrait`}
        />

        {/* Selection indicator - red pulsing border */}
        <div className="absolute inset-0 border-2 border-red-400 animate-pulse pointer-events-none" />
      </button>
    </div>
  )
}
